import {
    useState,
    useCallback,
    useEffect,
    Dispatch,
    SetStateAction
} from 'react';

const usePagination = (
    take: number,
    totalLength: number,
    setPreCursor: Dispatch<SetStateAction<number>>,
    setNextCursor: Dispatch<SetStateAction<number>>,
    setCursorOption: Dispatch<SetStateAction<boolean | null>>,
    lists: any[],
    id: string,
    modalCloseHandler?: () => void
) => {
    // 페이지 그룹 변화 시 게시물 수. 0, take * 10, take * 20 씩으로 증감시켜 사용
    const [pageGroupLength, setPageGroupLength] = useState(0);
    // 데이터 수를 10으로 나누 것. ex) 0, 10, 20..
    const [pageIndex, setPageIndex] = useState(0);
    // 버튼 disabled
    const [disabled, setDisabled] = useState({
        prevPage: false,
        nextPage: false,
        prevPageGroup: false,
        nextPageGroup: false
    });

    useEffect(() => {
        // prevPage
        if (pageGroupLength === 0 && pageIndex === 0) {
            setDisabled((prev) => ({ ...prev, prevPage: true }));
        } else {
            setDisabled((prev) => ({ ...prev, prevPage: false }));
        }
        // nextPage
        if (totalLength <= pageGroupLength + pageIndex + take) {
            setDisabled((prev) => ({ ...prev, nextPage: true }));
        } else {
            setDisabled((prev) => ({ ...prev, nextPage: false }));
        }
        // prevPageGroup
        if (pageGroupLength === 0) {
            setDisabled((prev) => ({ ...prev, prevPageGroup: true }));
        } else {
            setDisabled((prev) => ({ ...prev, prevPageGroup: false }));
        }
        // nextPageGroup
        if (totalLength - take * 10 <= pageGroupLength) {
            setDisabled((prev) => ({ ...prev, nextPageGroup: true }));
        } else {
            setDisabled((prev) => ({ ...prev, nextPageGroup: false }));
        }
    }, [pageGroupLength, pageIndex, take, totalLength]);

    const prevPageHandler = useCallback(() => {
        if (pageIndex === 0) {
            //이전 페이지 그룹으로 넘어가야되는 경우
            setPreCursor(lists[0][`${id}`] || 0);
            setCursorOption(false);
            setPageIndex(take * 9);
            setPageGroupLength((prev: number) => prev - take * 10);
        } else {
            //이전 페이지
            setPageIndex((prev: number) => prev - take);
        }
        modalCloseHandler?.();
    }, [
        modalCloseHandler,
        take,
        lists,
        pageIndex,
        setPageIndex,
        setPageGroupLength,
        setPreCursor,
        setCursorOption,
        id
    ]);

    const nextPageHandler = useCallback(() => {
        if (pageIndex === take * 9) {
            // 다음 페이지 그룹으로 넘어가야되는 경우
            setNextCursor(lists[lists.length - 1][`${id}`] || 0);
            setCursorOption(true);
            setPageGroupLength((prev: number) => prev + take * 10);
            setPageIndex(0);
        } else {
            // 다음 페이지
            setPageIndex((prev: number) => prev + take);
        }
        modalCloseHandler?.();
    }, [
        modalCloseHandler,
        take,
        lists,
        pageIndex,
        setPageIndex,
        setPageGroupLength,
        setNextCursor,
        setCursorOption,
        id
    ]);

    const prevPageGroupHandler = useCallback(() => {
        setPreCursor(lists[0][`${id}`] || 0);
        setCursorOption(false);
        setPageIndex(take * 9);
        // setPageIndex(0);
        setPageGroupLength((prev: number) => prev - take * 10);
        // }
        modalCloseHandler?.();
    }, [
        modalCloseHandler,
        take,
        lists,
        setPageIndex,
        setPageGroupLength,
        setPreCursor,
        setCursorOption,
        id
    ]);

    const nextPageGroupHandler = useCallback(() => {
        setNextCursor(lists[lists.length - 1][`${id}`] || 0);
        setCursorOption(true);
        setPageIndex(0);
        setPageGroupLength((prev: number) => prev + take * 10);
        modalCloseHandler?.();
    }, [
        modalCloseHandler,
        take,
        lists,
        setPageIndex,
        setPageGroupLength,
        setNextCursor,
        setCursorOption,
        id
    ]);

    const resetPaginationHandler = useCallback(() => {
        setPreCursor(0); // 페이지네이션 초기화
        setNextCursor(0);
        setCursorOption(null);
        setPageIndex(0);
        setPageGroupLength(0);
    }, [
        setPageIndex,
        setPageGroupLength,
        setPreCursor,
        setNextCursor,
        setCursorOption
    ]);

    return {
        pageIndex,
        setPageIndex,
        pageGroupLength,
        prevPageHandler,
        nextPageHandler,
        prevPageGroupHandler,
        nextPageGroupHandler,
        resetPaginationHandler,
        disabled
    };
};

export default usePagination;
